/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import UIButton from "../components/Button";
import "../styles/auth.css";

const baseUrl = process.env.REACT_APP_API_CONFIG;

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!token) {
        setValidating(false);
        return;
      }
      try {
        const res = await axios.post(`${baseUrl}/auth/verify-reset-token`, { token });
        if (!cancelled) setTokenValid(Boolean(res.data?.valid));
      } catch {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    };
    verify();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const canSubmit =
    password.length >= 8 &&
    password === confirmPassword &&
    tokenValid &&
    !loading;

  const handleSubmit = async () => {
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${baseUrl}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ||
          "Could not reset password. The link may have expired."
      );
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="px-6 py-8 max-w-md mx-auto text-color-desc">
        Verifying reset link...
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="flex flex-col gap-4 px-6 py-8 max-w-md mx-auto">
        <h1 className="text-2xl font-semibold text-white">Reset link invalid</h1>
        <p className="text-color-desc">
          This password-reset link is invalid or has expired. Request a new one
          from the login page.
        </p>
        <UIButton
          text="Back to login"
          onClick={() => navigate("/get-started")}
          backgroundColor="btn-color"
          textColor="text-base"
        />
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex flex-col gap-4 px-6 py-8 max-w-md mx-auto">
        <h1 className="text-2xl font-semibold text-white">Password updated</h1>
        <p className="text-color-desc">
          You can now sign in with your new password. All other active sessions
          have been signed out.
        </p>
        <UIButton
          text="Go to login"
          onClick={() => navigate("/get-started")}
          backgroundColor="btn-color"
          textColor="text-base"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-6 py-8 max-w-md mx-auto">
      <h1 className="text-2xl font-semibold text-white">Choose a new password</h1>
      <input
        type="password"
        placeholder="New password (min 8 chars)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="px-4 py-3 rounded-lg bg-[#2a2b2f] text-white outline-none border border-transparent focus:border-[#3FD4A2]"
        autoComplete="new-password"
      />
      <input
        type="password"
        placeholder="Confirm new password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        className="px-4 py-3 rounded-lg bg-[#2a2b2f] text-white outline-none border border-transparent focus:border-[#3FD4A2]"
        autoComplete="new-password"
      />
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <UIButton
        text={loading ? "Updating..." : "Update password"}
        onClick={handleSubmit}
        backgroundColor="btn-color"
        textColor="text-base"
        disabled={!canSubmit}
      />
    </div>
  );
};

export default ResetPassword;
